/**
 * The shared button — one source of truth for the app's button styling so the
 * primary CTA, secondary, danger, and ghost variants look identical everywhere
 * (they used to be inlined with drifting padding/weight/disabled treatment in
 * a dozen files).
 *
 * Two entry points:
 * - `<Button>` for `<button onClick>` actions.
 * - `buttonClass(...)` for the cases that must render as a `<Link>` /
 *   `DrillLaunchLink` (navigation) — pass it to their `className` so links and
 *   buttons share the exact same look.
 */

import type { ButtonHTMLAttributes, ReactNode } from "react";

export type ButtonVariant = "primary" | "secondary" | "danger" | "ghost";
export type ButtonSize = "sm" | "md";

const BASE =
  "inline-flex items-center justify-center gap-1.5 rounded-lg text-sm transition " +
  "disabled:opacity-50 disabled:cursor-not-allowed";

const SIZES: Record<ButtonSize, string> = {
  sm: "px-3.5 py-2",
  md: "px-4 py-3",
};

const VARIANTS: Record<ButtonVariant, string> = {
  // The accent CTA + the canonical "premium" shadow (an inset top highlight +
  // a soft accent-tinted drop). This rgba lived inline — with drifting values —
  // in ~8 files; it's now centralized here so every primary CTA matches.
  primary:
    "bg-accent text-white font-semibold hover:bg-accent/90 " +
    "shadow-[0_1px_0_rgba(255,255,255,0.18)_inset,0_8px_20px_-8px_rgba(124,147,241,0.5)]",
  secondary:
    "border border-border text-text font-medium hover:border-accent hover:bg-accent/10",
  danger: "bg-danger/15 text-danger font-medium hover:bg-danger/25",
  ghost: "text-text-muted font-medium hover:text-text",
};

export function buttonClass(opts?: {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  className?: string;
}): string {
  const { variant = "primary", size = "md", fullWidth, className } = opts ?? {};
  return [BASE, SIZES[size], VARIANTS[variant], fullWidth ? "w-full" : "", className ?? ""]
    .filter(Boolean)
    .join(" ");
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  children: ReactNode;
}

export function Button({
  variant,
  size,
  fullWidth,
  className,
  children,
  type = "button",
  ...rest
}: ButtonProps) {
  return (
    <button type={type} className={buttonClass({ variant, size, fullWidth, className })} {...rest}>
      {children}
    </button>
  );
}
